import { Avatar } from "../../atoms/avatar";
import { Icon } from "../../atoms/icons";
import { cn } from "../../lib";
import { nativeOnly } from "../../lib/web-role";
import { Box } from "../../primitives";
import { Image } from "../../primitives/image";
import { iconSize } from "../../tokens";
import { listItemIconColor, useListItemContext } from "./list-item-context";
import type {
  ListItemLeadingAvatarProps,
  ListItemLeadingIconProps,
  ListItemLeadingImageProps,
} from "./types";

// At the top of the row's content group (Figma: the element at y 0 in a frame
// the content's height), so it tops two lines of text; beside shorter content
// it sets the group's height and the content centres against it (DQ21)
const leadingSlotClasses = "flex items-center justify-center self-start";

// ── Icon ────────────────────────────────────────────────────
export const ListItemLeadingIcon = ({
  name,
  size = iconSize.lg,
  weight,
  color,
  className,
}: ListItemLeadingIconProps) => {
  const row = useListItemContext();
  return (
    <Box className={cn(leadingSlotClasses, className)}>
      <Icon name={name} size={size} weight={weight} color={listItemIconColor(row, color)} />
    </Box>
  );
};

ListItemLeadingIcon.displayName = "ListItem.LeadingIcon";

// ── Avatar ──────────────────────────────────────────────────
export const ListItemLeadingAvatar = ({ className, ...props }: ListItemLeadingAvatarProps) => {
  const { pressable } = useListItemContext();
  return (
    <Box
      className={cn(leadingSlotClasses, className)}
      // A pressable row is one element. On Android the avatar, an element of
      // its own, would be a second stop inside it, so hide it on native (iOS
      // still reads its name into the row's); web reads the row as it is
      aria-hidden={nativeOnly(pressable || undefined)}
    >
      <Avatar {...props} />
    </Box>
  );
};

ListItemLeadingAvatar.displayName = "ListItem.LeadingAvatar";

// ── Image ───────────────────────────────────────────────────
/**
 * `className` styles the slot's box, not the image (J3): to round the image,
 * give the box a radius with `overflow-hidden`. Size the image with `width`
 * and `height`, `aspectRatio` or `fill`.
 */
export const ListItemLeadingImage = ({ className, ...props }: ListItemLeadingImageProps) => (
  <Box className={cn(leadingSlotClasses, className)}>
    <Image {...(props as ListItemLeadingImageProps)} />
  </Box>
);

ListItemLeadingImage.displayName = "ListItem.LeadingImage";
